import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, TreeStructure, Buildings, Check, X } from "@phosphor-icons/react";
import { api, apiError } from "../lib/api";
import { useRealtime } from "../lib/realtime";
import { inputCls, btnPrimary, btnGhost } from "../components/checklists/shared";

function InlineName({ value, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!editing) return <button data-testid={`${testId}-edit`} onClick={() => setEditing(true)} className="font-bold text-slate-900 hover:underline text-left flex items-center gap-1">{value} <PencilSimple size={12} className="text-slate-400" /></button>;
  return (
    <span className="inline-flex items-center gap-1">
      <input data-testid={`${testId}-input`} value={v} onChange={(e) => setV(e.target.value)} className="h-8 px-2 rounded-md border border-slate-300 text-sm" autoFocus />
      <button data-testid={`${testId}-save`} onClick={() => { onSave(v); setEditing(false); }} className="p-1 rounded text-emerald-700 hover:bg-emerald-50"><Check size={16} weight="bold" /></button>
      <button onClick={() => { setV(value); setEditing(false); }} className="p-1 rounded text-slate-400 hover:bg-slate-100"><X size={16} /></button>
    </span>
  );
}

export default function OrganisationPage() {
  const [data, setData] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [newNode, setNewNode] = useState({ tier: 0, name: "", parent_id: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/hierarchy").then((r) => { setData(r.data); setTiers(r.data.tiers); }).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  useRealtime((m) => { if (m.type === "hierarchy.changed") load(); });

  if (!data) return <p className="text-slate-400">Loading organisation…</p>;
  const twoTier = data.tiers.length === 2;
  const leafTier = data.tiers.length - 1;
  const top = data.nodes.filter((n) => n.tier === 0);
  const leaves = data.nodes.filter((n) => n.tier === leafTier);
  const unassigned = data.venues.filter((v) => !v.node_id);
  const venuesOf = (nodeId) => data.venues.filter((v) => v.node_id === nodeId);

  const saveTiers = async (next) => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/hierarchy/tiers", { tiers: next });
      setData(d); setTiers(d.tiers);
      toast.success("Hierarchy tiers updated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggleDepth = () => {
    if (twoTier) {
      if (!window.confirm(`Switch to a single tier? All ${data.tiers[0].label} groups are removed and ${data.tiers[1].label}s become top-level.`)) return;
      saveTiers([tiers[1]]);
    } else {
      saveTiers([{ key: "cluster", label: "Cluster" }, tiers[0]]);
    }
  };
  const addNode = async () => {
    if (!newNode.name.trim()) return toast.error("Enter a name");
    try {
      await api.post("/hierarchy/nodes", { name: newNode.name, tier: Number(newNode.tier), parent_id: newNode.parent_id || null });
      setNewNode({ ...newNode, name: "" });
      toast.success("Group added"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const renameNode = async (id, name) => { try { await api.put(`/hierarchy/nodes/${id}`, { name }); load(); } catch (e) { toast.error(apiError(e)); } };
  const reparent = async (id, parent_id) => { try { await api.put(`/hierarchy/nodes/${id}`, parent_id ? { parent_id } : { clear_parent: true }); load(); } catch (e) { toast.error(apiError(e)); } };
  const removeNode = async (n) => {
    if (!window.confirm(`Delete "${n.name}"? Venues and sub-groups inside it become unassigned; team scopes referencing it are cleared.`)) return;
    try { await api.delete(`/hierarchy/nodes/${n.id}`); toast.success("Group deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const assignVenue = async (vid, node_id) => { try { await api.put(`/hierarchy/venues/${vid}`, { node_id: node_id || null }); load(); } catch (e) { toast.error(apiError(e)); } };
  const setPolicy = async (v) => { try { await api.put("/hierarchy/settings", { local_lines_min_tier: v }); toast.success("Local-line policy updated"); load(); } catch (e) { toast.error(apiError(e)); } };

  const VenueRow = ({ v }) => (
    <div data-testid={`org-venue-${v.id}`} className="flex items-center justify-between gap-2 py-1.5 pl-6 text-sm">
      <span className="flex items-center gap-2 text-slate-700"><Buildings size={14} className="text-slate-400" /> {v.name}</span>
      <select data-testid={`org-venue-node-${v.id}`} value={v.node_id || ""} onChange={(e) => assignVenue(v.id, e.target.value)} className="h-8 px-2 rounded-md border border-slate-200 text-xs bg-white">
        <option value="">Unassigned</option>
        {leaves.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );
  const LeafBlock = ({ n }) => (
    <div data-testid={`org-node-${n.id}`} className="border border-slate-200 rounded-xl p-3 bg-white">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{data.tiers[leafTier].label}</span>
          <InlineName value={n.name} onSave={(v) => renameNode(n.id, v)} testId={`org-node-name-${n.id}`} />
          <span className="text-xs text-slate-400">{n.venue_count} venue{n.venue_count === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-1">
          {twoTier && (
            <select data-testid={`org-node-parent-${n.id}`} value={n.parent_id || ""} onChange={(e) => reparent(n.id, e.target.value)} className="h-8 px-2 rounded-md border border-slate-200 text-xs bg-white">
              <option value="">No {data.tiers[0].label}</option>
              {top.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <button data-testid={`org-node-delete-${n.id}`} onClick={() => removeNode(n)} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash size={15} /></button>
        </div>
      </div>
      {venuesOf(n.id).map((v) => <VenueRow key={v.id} v={v} />)}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="organisation-page">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Multi-outlet structure</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2 flex items-center gap-3"><TreeStructure size={32} weight="duotone" className="text-sky-600" /> Organisation</h1>
        <p className="text-slate-500 text-sm mt-2">Define how outlets roll up — {data.tiers.map((t) => t.label).join(" → ")} → Venue. Team scopes, template distribution and the compliance dashboard follow this structure.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4" data-testid="org-tiers-card">
          <h2 className="font-heading text-lg font-bold">Hierarchy tiers</h2>
          {tiers.map((t, i) => (
            <div key={i}>
              <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Tier {i + 1} label {i === tiers.length - 1 ? "(venues attach here)" : "(top level)"}</label>
              <input data-testid={`org-tier-label-${i}`} value={t.label} onChange={(e) => setTiers(tiers.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className={`${inputCls} mt-1`} />
            </div>
          ))}
          <div className="flex gap-2 flex-wrap">
            <button data-testid="org-tiers-save" onClick={() => saveTiers(tiers)} disabled={busy} className={btnPrimary}>Save labels</button>
            <button data-testid="org-tiers-depth" onClick={toggleDepth} disabled={busy} className={btnGhost}>{twoTier ? "Use a single tier" : "Add a top tier"}</button>
          </div>
          <div className="pt-4 border-t border-slate-200">
            <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Who may add venue-local checklist lines</label>
            <select data-testid="org-local-policy" value={data.local_lines_min_tier} onChange={(e) => setPolicy(e.target.value)} className={`${inputCls} mt-1`}>
              <option value="venue">Store managers and above</option>
              <option value="region">{data.tiers[leafTier].label} managers and above</option>
              {twoTier && <option value="cluster">{data.tiers[0].label} managers and above</option>}
              <option value="brand">HQ only</option>
            </select>
            <p className="text-[11px] text-slate-400 mt-1">Roles also need the "Local lines" permission (Permissions → Brand roles).</p>
          </div>
        </section>

        <section className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="org-add-node">
            <h2 className="font-heading text-lg font-bold mb-3">Add a group</h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <select data-testid="org-new-tier" value={newNode.tier} onChange={(e) => setNewNode({ ...newNode, tier: e.target.value, parent_id: "" })} className={`${inputCls} sm:w-40`}>
                {data.tiers.map((t, i) => <option key={i} value={i}>{t.label}</option>)}
              </select>
              {twoTier && Number(newNode.tier) === 1 && (
                <select data-testid="org-new-parent" value={newNode.parent_id} onChange={(e) => setNewNode({ ...newNode, parent_id: e.target.value })} className={`${inputCls} sm:w-52`}>
                  <option value="">No {data.tiers[0].label} yet</option>
                  {top.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
              <input data-testid="org-new-name" value={newNode.name} onChange={(e) => setNewNode({ ...newNode, name: e.target.value })} placeholder={`${data.tiers[Number(newNode.tier)]?.label} name, e.g. Mumbai West`} className={inputCls} onKeyDown={(e) => e.key === "Enter" && addNode()} />
              <button data-testid="org-new-add" onClick={addNode} className={`${btnPrimary} flex items-center gap-1 shrink-0`}><Plus size={16} /> Add</button>
            </div>
          </div>

          <div className="space-y-3" data-testid="org-tree">
            {twoTier ? top.map((c) => (
              <div key={c.id} data-testid={`org-node-${c.id}`} className="border border-slate-300 rounded-2xl p-4 bg-slate-50 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{data.tiers[0].label}</span>
                    <InlineName value={c.name} onSave={(v) => renameNode(c.id, v)} testId={`org-node-name-${c.id}`} />
                    <span className="text-xs text-slate-400">{c.venue_count} venue{c.venue_count === 1 ? "" : "s"}</span>
                  </div>
                  <button data-testid={`org-node-delete-${c.id}`} onClick={() => removeNode(c)} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash size={15} /></button>
                </div>
                {leaves.filter((l) => l.parent_id === c.id).map((l) => <LeafBlock key={l.id} n={l} />)}
                {leaves.filter((l) => l.parent_id === c.id).length === 0 && <p className="text-xs text-slate-400 pl-1">No {data.tiers[1].label.toLowerCase()}s yet.</p>}
              </div>
            )) : null}
            {leaves.filter((l) => !twoTier || !l.parent_id).length > 0 && (
              <div className="space-y-2">
                {twoTier && <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400">{data.tiers[1].label}s without a {data.tiers[0].label}</p>}
                {leaves.filter((l) => !twoTier || !l.parent_id).map((l) => <LeafBlock key={l.id} n={l} />)}
              </div>
            )}
            <div className="border border-dashed border-slate-300 rounded-2xl p-4" data-testid="org-unassigned">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1">Unassigned venues ({unassigned.length})</p>
              {unassigned.map((v) => <VenueRow key={v.id} v={v} />)}
              {unassigned.length === 0 && <p className="text-xs text-slate-400">Every venue is assigned.</p>}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
